import React, { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Dialog,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
  Grid2,
  IconButton,
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import XIcon from '@mui/icons-material/X';
import { Employee } from '../../types';
import { ScrollFadeIn } from './utils/Utils';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';

export type EmployeeWithPhoto = Employee & {
  profilePictureUrl?: string;
};

interface StaffSectionProps {
  employees: EmployeeWithPhoto[];
  showBio?: boolean;
  showSocials?: boolean;
  showProfilePhoto?: boolean;
  resourcePluralLabel?: string;
}

type ResolvedSocials = {
  instagram?: string;
  facebook?: string;
  x?: string;
};

// ─── Social Link Button ───────────

interface SocialButtonProps {
  href: string;
  label: string;
  icon: React.ReactNode;
  variant?: 'dark' | 'light';
}

const SocialButton: React.FC<SocialButtonProps> = ({ href, label, icon, variant = 'dark' }) => (
  <IconButton
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    aria-label={label}
    size="small"
    sx={{
      width: 36,
      height: 36,
      borderRadius: '50%',
      border: variant === 'dark'
        ? '1px solid rgba(255,255,255,0.18)'
        : '1px solid rgba(255,255,255,0.3)',
      color: variant === 'dark' ? 'rgba(255,255,255,0.75)' : 'rgba(255,255,255,0.9)',
      bgcolor: variant === 'dark'
        ? 'rgba(255,255,255,0.06)'
        : 'rgba(255,255,255,0.12)',
      backdropFilter: 'blur(8px)',
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: 'rgba(255,255,255,0.22)',
        color: '#fff',
        borderColor: 'rgba(255,255,255,0.5)',
        transform: 'translateY(-2px)',
      },
    }}
  >
    {icon}
  </IconButton>
);

// ─── Main Component ────────────────────────────────────────────────────────────

const StaffSection: React.FC<StaffSectionProps> = ({
  employees,
  showBio = true,
  showSocials = true,
  showProfilePhoto = true,
  resourcePluralLabel = 'Staff',
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeWithPhoto | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const shouldUseCarousel = isDesktop ? employees.length > 4 : employees.length > 2;

  const formatName = (firstName?: string, lastName?: string) => {
    const cap = (v?: string) => (!v ? '' : v.charAt(0).toUpperCase() + v.slice(1));
    return `${cap(firstName)}${lastName ? ` ${cap(lastName)}` : ''}`.trim();
  };

  const buildBookNowLink = (id: string) =>
    `${window.location.origin}/book-now?employee=${encodeURIComponent(id)}`;

  const toExternalUrl = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed.replace(/^\/+/, '')}`;
  };

  const toInstagramUrl = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const handle = trimmed
      .replace(/^@+/, '')
      .replace(/^www\./i, '')
      .replace(/^instagram\.com\//i, '')
      .split('/')[0]
      .split('?')[0]
      .trim();
    return handle ? `https://instagram.com/${handle}` : undefined;
  };

  const toXUrl = (value?: string) => {
    if (!value) return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const handle = trimmed
      .replace(/^@+/, '')
      .replace(/^www\./i, '')
      .replace(/^x\.com\//i, '')
      .replace(/^twitter\.com\//i, '')
      .split('/')[0]
      .split('?')[0]
      .trim();
    return handle ? `https://x.com/${handle}` : undefined;
  };

  const resolveSocials = (e: EmployeeWithPhoto): ResolvedSocials => ({
    instagram: toInstagramUrl(e.socials?.instagram),
    facebook: toExternalUrl(e.socials?.facebook),
    x: toXUrl(e.socials?.twitter),
  });

  const hasSocials = (e: EmployeeWithPhoto) => {
    if (!showSocials) return false;
    const socials = resolveSocials(e);
    return Boolean(socials.instagram || socials.facebook || socials.x);
  };

  if (!employees.length) return null;

  const handleCardClick = (employee: EmployeeWithPhoto) => {
    if ((!showBio || !employee.bio) && !hasSocials(employee)) {
      window.location.assign(buildBookNowLink(employee.id));
      return;
    }
    setSelectedEmployee(employee);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedEmployee(null);
  };

  // ─── Employee Card ───────────────────────────────────────────────────────────

  const renderEmployeeCard = (employee: EmployeeWithPhoto, index = 0) => {
    const hasPhoto = Boolean(employee.profilePictureUrl) && showProfilePhoto;
    const socials = resolveSocials(employee);
    const hasDetails = Boolean(showBio && employee.bio) || Boolean(showSocials && (socials.instagram || socials.facebook || socials.x));
    const isClickable = true;

    return (
      <Card
        elevation={0}
        sx={{
          borderRadius: '16px',
          overflow: 'hidden',
          position: 'relative',
          height: '100%',
          border: 'none',
          boxShadow: '0 24px 48px rgba(0,0,0,0.4)',
          bgcolor: '#0c111d',
          color: '#fff',
          animationDelay: `${index * 80}ms`,
        }}
      >
        <CardActionArea
          onClick={() => handleCardClick(employee)}
          disabled={!isClickable}
          disableRipple={!isClickable}
          sx={{
            height: '100%',
            p: 0,
            cursor: isClickable ? 'pointer' : 'default',
            '&:hover .card-bg': isClickable
              ? { transform: 'scale(1.07)', filter: hasPhoto ? 'brightness(0.82)' : 'none' }
              : {},
            '&:hover .card-overlay': isClickable
              ? {
                background:
                  'linear-gradient(0deg, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.42) 52%, rgba(0,0,0,0.08) 100%)',
              }
              : {},
            '&:hover .card-cta': isClickable ? { opacity: 1, transform: 'translateY(0)' } : {},
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 260, md: 340 },
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            {/* Background */}
            <Box
              className="card-bg"
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: hasPhoto
                  ? `url(${employee.profilePictureUrl})`
                  : 'radial-gradient(ellipse at 30% 20%, #1e293b 0%, #0f172a 50%, #080d1a 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center top',
                filter: hasPhoto ? 'brightness(0.92)' : 'none',
                transition: 'transform 0.55s cubic-bezier(0.4,0,0.2,1), filter 0.55s ease',
              }}
            />

            {/* Grain overlay for texture */}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E")`,
                opacity: 0.6,
                pointerEvents: 'none',
              }}
            />

            {/* Initials avatar (no photo) */}
            {!hasPhoto && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  pb: 5,
                }}
              >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    border: '1.5px solid rgba(255,255,255,0.2)',
                    bgcolor: 'rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(12px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.75rem',
                    fontWeight: 300,
                    color: 'rgba(255,255,255,0.6)',
                    fontFamily: '"DM Serif Display", serif',
                    letterSpacing: 2,
                  }}
                >
                  {(employee.firstName?.charAt(0) ?? '?').toUpperCase()}
                </Box>
              </Box>
            )}

            {/* Gradient overlay */}
            <Box
              className="card-overlay"
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(0deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.24) 48%, rgba(0,0,0,0.0) 100%)',
                transition: 'background 0.4s ease',
              }}
            />

            {/* Content */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                p: 2,
                pb: 2.25,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"DM Serif Display", serif',
                  fontSize: { xs: '1rem', md: '1.125rem' },
                  fontWeight: 400,
                  color: '#fff',
                  lineHeight: 1.2,
                  letterSpacing: 0.2,
                }}
              >
                {formatName(employee.firstName, employee.lastName)}
              </Typography>

              {/* CTA row */}
              {isClickable && (
                <Box
                  className="card-cta"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    opacity: 0.65,
                    transform: 'translateY(4px)',
                    transition: 'opacity 0.3s ease, transform 0.3s ease',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      letterSpacing: 1.6,
                      textTransform: 'uppercase',
                      color: '#c8d8ff',
                    }}
                  >
                    {hasDetails ? 'View profile' : 'Book now'}
                  </Typography>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M1 9L9 1M9 1H3M9 1V7" stroke="#c8d8ff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Box>
              )}

              {!isClickable && (
                <Typography
                  sx={{
                    fontSize: '0.7rem',
                    color: 'rgba(255,255,255,0.4)',
                    letterSpacing: 0.5,
                  }}
                >
                  Team member
                </Typography>
              )}

            </Box>
          </Box>
        </CardActionArea>
      </Card>
    );
  };

  // ─── Grid / Carousel ─────────────────────────────────────────────────────────

  const renderCardGrid = () => (
    <Grid2 container spacing={2} justifyContent="center">
      {employees.map((employee, i) => (
        <Grid2 key={employee.id} size={{ xs: 6, sm: 6, md: 3, lg: 3 }}>
          {renderEmployeeCard(employee, i)}
        </Grid2>
      ))}
    </Grid2>
  );

  const renderCarousel = () => {
    const sm = theme.breakpoints.values.sm || 600;
    const md = theme.breakpoints.values.md || 900;
    return (
      <Swiper
        modules={[Navigation]}
        navigation
        spaceBetween={16}
        slidesPerView={Math.min(2, employees.length)}
        breakpoints={{
          0: { slidesPerView: Math.min(2, employees.length) },
          [sm]: { slidesPerView: Math.min(3, employees.length) },
          [md]: { slidesPerView: Math.min(4, employees.length) },
        }}
      >
        {employees.map((employee, i) => (
          <SwiperSlide key={employee.id}>
            <Box sx={{ height: '100%', px: 1, pb: 2 }}>
              {renderEmployeeCard(employee, i)}
            </Box>
          </SwiperSlide>
        ))}
      </Swiper>
    );
  };

  // ─── Dialog ──────────────────────────────────────────────────────────────────

  const renderDialog = () => {
    if (!selectedEmployee) return null;
    const emp = selectedEmployee;
    const hasPhoto = Boolean(emp.profilePictureUrl) && showProfilePhoto;
    const socials = resolveSocials(emp);
    const name = formatName(emp.firstName, emp.lastName);

    return (
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        aria-labelledby="employee-dialog-title"
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            bgcolor: '#0c111d',
            boxShadow: '0 40px 80px rgba(0,0,0,0.7)',
            border: '1px solid rgba(255,255,255,0.07)',
            m: { xs: 2, sm: 3 },
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: 'rgba(0,0,0,0.75)',
              backdropFilter: 'blur(6px)',
            },
          },
        }}
      >
        {/* Hero photo section */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: 270, sm: 340 },
            overflow: 'hidden',
          }}
        >
          {/* Background */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: hasPhoto
                ? `url(${emp.profilePictureUrl})`
                : 'radial-gradient(ellipse at 30% 20%, #1e293b 0%, #0f172a 60%, #080d1a 100%)',
              backgroundSize: 'cover',
              backgroundPosition: 'center top',
              filter: hasPhoto ? 'brightness(0.82)' : 'none',
            }}
          />

          {/* Grain */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.05'/%3E%3C/svg%3E")`,
              opacity: 0.5,
              pointerEvents: 'none',
            }}
          />

          {/* Gradient */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(0deg, #0c111d 0%, rgba(12,17,29,0.2) 60%, rgba(12,17,29,0.0) 100%)',
            }}
          />

          {/* Initials if no photo */}
          {!hasPhoto && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pb: 4,
              }}
            >
              <Box
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: '50%',
                  border: '1.5px solid rgba(255,255,255,0.15)',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '2rem',
                  fontWeight: 300,
                  color: 'rgba(255,255,255,0.55)',
                  fontFamily: '"DM Serif Display", serif',
                }}
              >
                {(emp.firstName?.charAt(0) ?? '?').toUpperCase()}
              </Box>
            </Box>
          )}

          {/* Close button */}
          <IconButton
            onClick={handleDialogClose}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              bgcolor: 'rgba(0,0,0,0.5)',
              border: '1px solid rgba(255,255,255,0.15)',
              color: 'rgba(255,255,255,0.8)',
              backdropFilter: 'blur(8px)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.75)', color: '#fff' },
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
            </svg>
          </IconButton>

        </Box>

        {/* Content section */}
        <Box sx={{ px: 2.5, pt: 2, pb: 2.5 }}>
          <Typography
            id="employee-dialog-title"
            sx={{
              fontFamily: '"DM Serif Display", serif',
              fontSize: { xs: '1.5rem', sm: '1.75rem' },
              fontWeight: 400,
              color: '#fff',
              lineHeight: 1.15,
              letterSpacing: 0.3,
              mb: (showBio && emp.bio) || hasSocials(emp) ? 1.25 : 0,
            }}
          >
            {name}
          </Typography>

          {/* Bio */}
          {showBio && emp.bio && (
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: 'rgba(255,255,255,0.65)',
                lineHeight: 1.7,
                mb: hasSocials(emp) ? 2 : 0,
              }}
            >
              {emp.bio}
            </Typography>
          )}

          {/* Socials */}
          {showSocials && hasSocials(emp) && (
            <Box>
              <Typography
                sx={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: 1.8,
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.3)',
                  mb: 1,
                }}
              >
                Follow
              </Typography>
              <Stack direction="row" spacing={1}>
                {socials.instagram && (
                  <SocialButton
                    href={socials.instagram}
                    label="Instagram"
                    icon={<InstagramIcon fontSize="small" />}
                    variant="light"
                  />
                )}
                {socials.facebook && (
                  <SocialButton
                    href={socials.facebook}
                    label="Facebook"
                    icon={<FacebookIcon fontSize="small" />}
                    variant="light"
                  />
                )}
                {socials.x && (
                  <SocialButton
                    href={socials.x}
                    label="X / Twitter"
                    icon={<XIcon fontSize="small" />}
                    variant="light"
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* Divider line */}
          <Box
            sx={{
              height: '1px',
              bgcolor: 'rgba(255,255,255,0.07)',
              my: 2,
            }}
          />

          {/* Book button */}
          <Button
            component="a"
            href={buildBookNowLink(emp.id)}
            fullWidth
            disableElevation
            sx={{
              py: 1.4,
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              letterSpacing: 0.4,
              color: '#0c111d',
              bgcolor: '#e8eef8',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#fff',
                transform: 'translateY(-1px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
              },
            }}
          >
            Book with {emp.firstName}
          </Button>
        </Box>
      </Dialog>
    );
  };

  // ─── Section ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Box
        id="our-staff"
        px={{ xs: 2, md: 2 }}
        pt={6}
        pb={6}
        sx={{ backgroundColor: theme.palette.background.default }}
      >
        <ScrollFadeIn>
          <Box maxWidth="lg" mx="auto">
            {/* Section header */}
            <Box mb={5} textAlign="center">
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1.5,
                }}
              >
                Our {resourcePluralLabel}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  letterSpacing: 0.3,
                  maxWidth: 420,
                  mx: 'auto',
                  lineHeight: 1.7,
                }}
              >
                Meet the team dedicated to making every visit exceptional.
              </Typography>
            </Box>

            {shouldUseCarousel ? renderCarousel() : renderCardGrid()}
          </Box>
        </ScrollFadeIn>
      </Box>

      {renderDialog()}
    </>
  );
};

export default StaffSection;
